import { Avatar, Button, Card, CardContent, Chip, TextField, Typography } from '@mui/material';
import { useDispatch } from 'react-redux';
import { withStyles } from '@mui/styles';
import PropTypes from 'prop-types';
import React, { useEffect, useState } from 'react';
import moment from 'moment';

import { DialogContainerComponent } from '../layout/dialogContainer.component';
import { REMOVE_SIMPLIFED_PATIENT_ID_SUCCESS, UPDATE_USERS_GROUPS_SUCCESS, removeSimplifedPatientId, updateUserGroups } from './users.actions';
import { first, get } from 'lodash';
import { handleToastMessage } from '../layout/layout.actions';
import Colors from '../../styles/colors';

const formatTime = (dateString) => moment.utc(dateString).format('M/D/YY h:mma z');

const Field = ({ label, children }) => {
  if (!children) return null;
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      <Typography variant="caption" style={{ color: '#888', textTransform: 'uppercase', letterSpacing: '0.05em', fontWeight: 600 }}>{label}</Typography>
      <Typography variant="body2" style={{ color: '#1a1a1a' }}>{children}</Typography>
    </div>
  );
};

Field.propTypes = {
  label: PropTypes.string,
  children: PropTypes.node,
};

const Section = ({ title, children }) => (
  <Card variant="outlined" style={{ borderRadius: 12 }}>
    <CardContent style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      {title && (  <Typography variant="overline" style={{ color: '#555', fontWeight: 700, lineHeight: 1 }}>{title}</Typography> )}
      {children}
    </CardContent>
  </Card>
);

Section.propTypes = {
  title: PropTypes.string,
  children: PropTypes.node,
};

const UserDetails = ({ classes, user }) => {
  const [userGroups, setUserGroups] = useState(user.groups?.join(', ') || '');
  const [isRemoveDialogOpen, setIsRemoveDialogOpen] = useState(false);
  const dispatch = useDispatch();

  useEffect(() => {
    setUserGroups(user.groups?.join(', ') || '');
  }, [user.groups]);

  const onUserGroupSave = async () => {
    const response = await dispatch(updateUserGroups(user.id, userGroups.split(',').map(s => s.trim())));
    if (response.type === UPDATE_USERS_GROUPS_SUCCESS) {
      dispatch(handleToastMessage('User groups updated.'));
    } else {
      const message = first(get(response, 'messages', []));
      dispatch(handleToastMessage(`Failed to update user groups: ${message}`, true));
    }
  };

  const onRemoveSimplifedPatientId = async () => {
    const response = await dispatch(removeSimplifedPatientId(user.id));
    if (response.type === REMOVE_SIMPLIFED_PATIENT_ID_SUCCESS) {
      dispatch(handleToastMessage('Simplifed patient ID removed.'));
    } else {
      const message = first(get(response, 'messages', []));
      dispatch(handleToastMessage(`Failed to remove Simplifed patient ID: ${message}`, true));
    }
  };

  const initials = `${user.firstName?.[0] || ''}${user.lastName?.[0] || ''}`.toUpperCase();

  return (
    <>
      <div className={classes.page}>
        <div className={classes.header}>
          <Avatar sx={{ width: 56, height: 56, fontSize: '1.4rem', backgroundColor: Colors.primary.main }}>{initials}</Avatar>
          <div>
            <Typography variant="h5" className={classes.userName}>
              {user.firstName} {user.lastName}
            </Typography>
            {(user.canDebug) && (
              <div className={classes.chips}>
                {user.canDebug && <Chip size="small" label="Debug BLE" color="primary" />}
              </div>
            )}
          </div>
        </div>

        <div className={classes.grid}>
          <Section title="Contact & Identity">
            <Field label="Email"> <a href={`mailto:${user.email}`} className={classes.link}>{user.email}</a></Field>
            <Field label="Phone">{user.phoneNumber}</Field>
            <Field label="User ID">{user.id}</Field>
            <Field label="Firebase ID">{user.firebaseId}</Field>
          </Section>

          <Section title="Activity">
            <Field label="Registered">{formatTime(user.createdTime)}</Field>
            <Field label="Last Login">{formatTime(user.lastLoginTime)}</Field>
            <Field label="Last Known Location">
              {user.lastKnownPosition ? (() => {
                const [lng, lat] = user.lastKnownPosition.coordinates;
                return (
                  <>
                    <a href={`https://www.google.com/maps?q=${lat},${lng}`} target="_blank" className={classes.link}>
                      {lat}, {lng}
                    </a>
                    {' '}({formatTime(user.lastKnownPositionTime)})
                  </>
                );
              })() : <span className={classes.muted}>Unknown</span>}
            </Field>
          </Section>

          <Section title="Simplifed">
            <Field label="Patient ID">{user.simplifedPatientId  ? user.simplifedPatientId : <span className={classes.muted}>None</span>}</Field>
            {user.simplifedPatientId && (
              <>
                <Field label="Intake Form Submitted">
                  {user.patientInfo?.isIntakeFormSubmitted ? 'Yes' : 'No'}
                </Field>
                <Field label="Insurance Eligible">
                  {user.patientInfo?.isInsuranceEligible ? 'Yes' : 'No'}
                </Field>
                <div>
                  <Button size="small" variant="outlined" color="error" onClick={() => setIsRemoveDialogOpen(true)}>
                    Remove associated simplifed patient
                  </Button>
                </div>
              </>
            )}
          </Section>

          <Section title="Groups">
            <Field label="Comma separated">
              <div className={classes.groupsRow}>
                <TextField
                  fullWidth
                  size="small"
                  variant="outlined"
                  value={userGroups}
                  onChange={(e) => setUserGroups(e.target.value)}
                />
                <Button variant="contained" onClick={onUserGroupSave}>Save</Button>
              </div>
            </Field>
          </Section>
        </div>
      </div>

      <DialogContainerComponent
        isOpen={isRemoveDialogOpen}
        onClose={() => setIsRemoveDialogOpen(false)}
        onSubmit={onRemoveSimplifedPatientId}
        isSubmitDisabled={false}
        title="Remove Simplifed Patient ID"
        submitButtonText="Remove"
      >
        <Typography>Are you sure you want to remove the Simplifed patient ID from this user?</Typography>
      </DialogContainerComponent>
    </>
  );
};

UserDetails.propTypes = {
  classes: PropTypes.object.isRequired,
  user: PropTypes.object.isRequired,
};

const styles = {
  page: {
    padding: '2rem',
    maxWidth: 960,
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    gap: '1rem',
    marginBottom: '2rem',
  },
  userName: {
    fontWeight: '700',
  },
  chips: {
    display: 'flex',
    gap: 8,
    marginTop: 6,
  },
  grid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(2, 1fr)',
    gap: '1rem',
  },
  link: {
    color: Colors.primary.main,
  },
  muted: {
    color: Colors.primary.dark,
  },
  groupsRow: {
    display: 'flex',
    gap: 8,
    alignItems: 'center',
    marginTop: 4,
  },
};

export default withStyles(styles)(UserDetails);
